import { StatsigProviderExpo } from '@statsig/expo-bindings';
import Constants from 'expo-constants';
import * as Device from 'expo-device';
import * as Notifications from 'expo-notifications';
import * as Updates from 'expo-updates';
import * as React from 'react';
import { LogBox, Platform, StyleSheet, Text } from 'react-native';
import { GestureHandlerRootView } from 'react-native-gesture-handler';
import 'react-native-get-random-values';
import { SafeAreaProvider } from 'react-native-safe-area-context';
import { enableScreens } from 'react-native-screens';
import { Provider } from 'react-redux';
import { v4 as uuidv4 } from 'uuid';

import ErrorBoundary from './components/errorBoundaries/ErrorBoundary';
import { LocationsServiceProvider } from './components/services/LocationsServiceContext';
import { PlentifulServiceContextProvider } from './components/services/PlentifulServiceContext';
import { ReviewServiceContextProvider } from './components/services/ReviewServiceContext';
import { EnvVars } from './constants/EnvConfig';
import MainContainer from './navigation/MainContainer';
import { ExpoModulesProvider } from './providers/ExpoModulesProvider';
import { TranslationProvider } from './providers/TranslationProvider';
import { ErrorScreen } from './screens/ErrorScreen';
import Store from './store/Store';
import { debug, log, logError } from './utils/logger';

let Sentry;
if (Platform.OS === 'web') {
  Sentry = require('@sentry/react');
} else {
  Sentry = require('@sentry/react-native');
}

debug('hello, world');
log('uuid', { uuid: uuidv4() });

enableScreens();

// Suppress LogBox during testing when REACT_NATIVE_LOGBOX_DISABLED is set
if (__DEV__) {
  if (process.env.REACT_NATIVE_LOGBOX_DISABLED === 'true') {
    console.log('LogBox is DISABLED for testing');
    LogBox.ignoreAllLogs(true);
  } else {
    console.log('LogBox is ENABLED');
  }
}

// disable font scaling for Text components
// https://stackoverflow.com/a/51414341/6118172
Text.defaultProps = Text.defaultProps || {};
Text.defaultProps.allowFontScaling = false;

// Hotfix for react-native-reanimated see: https://github.com/software-mansion/react-native-reanimated/issues/3355#issuecomment-1518942329
if (typeof window !== 'undefined') {
  window._frameTimestamp = null;
}

if (EnvVars.useSentry) {
  // initialize sentry
  Sentry.init({
    dsn: EnvVars.sentryDsn,
    environment: EnvVars.sentryEnv,
    debug: EnvVars.sentryEnv != 'production',
  });
}

const styles = StyleSheet.create({
  view: {
    flex: 1,
  },
});

const restartApp = async () => {
  if (Platform.OS === 'web') {
    location.reload();
  } else {
    try {
      await Updates.reloadAsync();
    } catch (error) {
      logError(new Error('Failed to reload app', { cause: error }));
    }
  }
};

function App({
  MainContainerComponent = MainContainer,
  StatsigProviderComponent = StatsigProviderExpo,
  ErrorScreenComponent = ErrorScreen,
} = {}) {
  const expoModules = {
    Updates,
    Constants,
    Device,
    Notifications,
  };

  const mainContainerWithErrorBoundary = (
    <ErrorBoundary fallback={<ErrorScreenComponent restartCallback={restartApp} />}>
      <ExpoModulesProvider modules={expoModules}>
        <TranslationProvider>
          <PlentifulServiceContextProvider envVars={EnvVars}>
            <ReviewServiceContextProvider>
              <LocationsServiceProvider>
                <GestureHandlerRootView style={styles.view}>
                  <MainContainerComponent />
                </GestureHandlerRootView>
              </LocationsServiceProvider>
            </ReviewServiceContextProvider>
          </PlentifulServiceContextProvider>
        </TranslationProvider>
      </ExpoModulesProvider>
    </ErrorBoundary>
  );

  return (
    <Provider store={Store}>
      <SafeAreaProvider>
        <ErrorBoundary fallback={mainContainerWithErrorBoundary}>
          <StatsigProviderComponent
            sdkKey={EnvVars.statsigSdkKey}
            // Anonymous until login (stableId is tied to each device and is
            // usable for experimentation). `custom.host` lets gate rules
            // target a specific web hostname — e.g. the fano-demo
            // CloudFront distribution — without paying for a new Statsig
            // environment tier.
            user={{
              custom:
                Platform.OS === 'web' && typeof window !== 'undefined' && window.location?.hostname
                  ? { host: window.location.hostname }
                  : {},
            }}
            options={{ environment: { tier: EnvVars.statsigEnv } }}
          >
            {mainContainerWithErrorBoundary}
          </StatsigProviderComponent>
        </ErrorBoundary>
      </SafeAreaProvider>
    </Provider>
  );
}

export default App;
